import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  UnitMeasureInfo,
  MaterialBasicInfo,
} from './interfaces/unit-measure-display.interface';
import { environment } from 'environments/environment.prod';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

/**
 * Unit Measure Display Component
 *
 * Reusable component for displaying unit of measure information (UMB/UMP) consistently
 * across the application. Supports intelligent display logic, flexible validation modes,
 * and historical data preservation.
 *
 * @example
 * ```html
 * <app-unit-measure-display
 *   [baseUnit]="{ quantity: 288, unitId: '...', unitName: 'Unidades', unitAbbreviation: 'UND' }"
 *   [parallelUnit]="{ quantity: 36.5, unitId: '...', unitName: 'Libras', unitAbbreviation: 'lb' }"
 *   [material]="{ _id: '...', code: 'MAT-001', UMPActive: true }"
 *   [companyUMPActive]="true"
 * ></app-unit-measure-display>
 * ```
 *
 * @see {@link UnitMeasureInfo} for unit data structure
 * @see {@link MaterialBasicInfo} for material data structure
 */
@Component({
  selector: 'app-unit-measure-display',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './unit-measure-display.component.html',
  styleUrl: './unit-measure-display.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DecimalPipe],
})
export class UnitMeasureDisplayComponent implements OnInit {
  /**
   * Optional: Unit of measure that was used/selected (e.g., sold unit, commercial unit)
   * Only displayed if different from baseUnit to avoid redundancy
   */
  @Input() usedUnit?: UnitMeasureInfo;

  /**
   * Required: Base unit of measure (UMB)
   * Always displayed as the primary reference unit
   */
  @Input({ required: true }) baseUnit!: UnitMeasureInfo;

  /**
   * Optional: Parallel unit of measure (UMP)
   * Displayed based on validation mode and data availability
   */
  @Input() parallelUnit?: UnitMeasureInfo;

  /**
   * Required: Material information for business logic
   * Used to determine if material has UMP enabled
   */
  @Input({ required: true }) material!: MaterialBasicInfo;

  /**
   * Required: Company-level flag for parallel unit support
   * Used in strict validation mode
   */
  @Input({ required: true }) companyUMPActive!: boolean;

  /**
   * Optional: Enable strict flag validation mode
   *
   * - false (default): Show UMP if data exists (preserves historical information)
   * - true: Only show UMP if both companyUMPActive and material.UMPActive are true
   *
   * @default false
   */
  @Input() validateFlags: boolean = false;

  /**
   * Optional: Number of decimal places for quantity display
   * @default 3
   */
  @Input() decimalPlaces: number = 3;

  /**
   * Optional: Enable tooltips with conversion information
   * @default true
   */
  @Input() showTooltips: boolean = true;

  /**
   * Optional: Compact display mode with smaller font and spacing
   * Useful for dense tables or mobile views
   * @default false
   */
  @Input() compactMode: boolean = true;

  /**
   * Optional: Status ID for applying conditional styling
   * When statusId equals StatusEnum.INACTIVE, applies strikethrough with red color
   * @default undefined
   */
  @Input() statusId?: string | number;

  /**
   * Expose StatusEnum to template for conditional styling
   */
  readonly StatusEnum = StatusEnum;

  constructor(private decimalPipe: DecimalPipe) {}

  /**
   * Angular lifecycle hook: Initialize component
   * Runs development-only validations to catch incorrect component usage
   */
  ngOnInit(): void {
    // Only run validations in development mode (zero performance impact in production)
    if (!environment.production) {
      this.runDevelopmentValidations();
    }
  }

  /**
   * Executes all development validations
   * Only called when environment.production = false
   * @private
   */
  private runDevelopmentValidations(): void {
    this.validateCriticalInputs();
    this.validateWarnings();
    this.validateDataCoherence();
  }

  /**
   * CRITICAL VALIDATIONS (console.error)
   * These indicate errors that block correct functionality
   * @private
   */
  private validateCriticalInputs(): void {
    const LOG_PREFIX = '[UnitMeasureDisplay]';

    // 1. Validate baseUnit
    if (!this.baseUnit) {
      console.error(`${LOG_PREFIX} CRITICAL: baseUnit is required but not provided.`);
      return; // Don't continue validating if baseUnit is null
    }

    if (!this.isValidQuantity(this.baseUnit.quantity)) {
      console.error(
        `${LOG_PREFIX} CRITICAL: baseUnit.quantity is invalid (null, undefined, NaN, or not a number).`,
        { received: this.baseUnit.quantity, type: typeof this.baseUnit.quantity }
      );
    }

    if (!this.baseUnit.unitId || this.baseUnit.unitId.trim() === '') {
      console.error(
        `${LOG_PREFIX} CRITICAL: baseUnit.unitId is required but empty or invalid.`,
        { received: this.baseUnit.unitId }
      );
    }

    if (!this.baseUnit.unitName || this.baseUnit.unitName.trim() === '') {
      console.error(
        `${LOG_PREFIX} CRITICAL: baseUnit.unitName is required but empty.`,
        { received: this.baseUnit.unitName }
      );
    }

    if (!this.baseUnit.unitAbbreviation || this.baseUnit.unitAbbreviation.trim() === '') {
      console.error(
        `${LOG_PREFIX} CRITICAL: baseUnit.unitAbbreviation is required but empty.`,
        { received: this.baseUnit.unitAbbreviation }
      );
    }

    // 2. Validate material
    if (!this.material) {
      console.error(`${LOG_PREFIX} CRITICAL: material is required but not provided.`);
      return; // Don't continue validating if material is null
    }

    if (!this.material._id || this.material._id.trim() === '') {
      console.error(
        `${LOG_PREFIX} CRITICAL: material._id is required but empty.`,
        { received: this.material._id }
      );
    }

    if (!this.material.code || this.material.code.trim() === '') {
      console.error(
        `${LOG_PREFIX} CRITICAL: material.code is required but empty.`,
        { received: this.material.code }
      );
    }

    if (typeof this.material.UMPActive !== 'boolean') {
      console.error(
        `${LOG_PREFIX} CRITICAL: material.UMPActive must be a boolean.`,
        {
          received: this.material.UMPActive,
          type: typeof this.material.UMPActive,
          suggestion: 'Use "?? false" to provide a default value if the property might be undefined'
        }
      );
    }

    // 3. Validate companyUMPActive
    if (typeof this.companyUMPActive !== 'boolean') {
      console.error(
        `${LOG_PREFIX} CRITICAL: companyUMPActive must be a boolean.`,
        {
          received: this.companyUMPActive,
          type: typeof this.companyUMPActive
        }
      );
    }

    // 4. Validate parallelUnit if present
    if (this.parallelUnit) {
      if (this.isValidQuantity(this.parallelUnit.quantity)) {
        // Has valid quantity, validate the rest
        if (!this.parallelUnit.unitId || this.parallelUnit.unitId.trim() === '') {
          console.error(
            `${LOG_PREFIX} CRITICAL: parallelUnit.unitId is required when parallelUnit is provided.`,
            { received: this.parallelUnit.unitId }
          );
        }

        if (!this.parallelUnit.unitName || this.parallelUnit.unitName.trim() === '') {
          console.error(
            `${LOG_PREFIX} CRITICAL: parallelUnit.unitName is required when parallelUnit is provided.`,
            { received: this.parallelUnit.unitName }
          );
        }

        if (!this.parallelUnit.unitAbbreviation || this.parallelUnit.unitAbbreviation.trim() === '') {
          console.error(
            `${LOG_PREFIX} CRITICAL: parallelUnit.unitAbbreviation is required when parallelUnit is provided.`,
            { received: this.parallelUnit.unitAbbreviation }
          );
        }
      }
    }
  }

  /**
   * WARNING VALIDATIONS (console.warn)
   * These indicate unusual situations but don't block functionality
   * @private
   */
  private validateWarnings(): void {
    const LOG_PREFIX = '[UnitMeasureDisplay]';

    // 1. Negative quantities (rare but technically allowed)
    if (this.baseUnit && this.isValidQuantity(this.baseUnit.quantity) && this.baseUnit.quantity < 0) {
      console.warn(
        `${LOG_PREFIX} WARNING: baseUnit.quantity is negative. This is unusual for unit measures.`,
        { quantity: this.baseUnit.quantity }
      );
    }

    if (this.parallelUnit && this.isValidQuantity(this.parallelUnit.quantity) && this.parallelUnit.quantity < 0) {
      console.warn(
        `${LOG_PREFIX} WARNING: parallelUnit.quantity is negative. This is unusual for unit measures.`,
        { quantity: this.parallelUnit.quantity }
      );
    }

    // 2. Extreme values (Infinity, -Infinity)
    if (this.baseUnit && this.isValidQuantity(this.baseUnit.quantity) && !isFinite(this.baseUnit.quantity)) {
      console.warn(
        `${LOG_PREFIX} WARNING: baseUnit.quantity is Infinity or -Infinity.`,
        { quantity: this.baseUnit.quantity }
      );
    }

    if (this.parallelUnit && this.isValidQuantity(this.parallelUnit.quantity) && !isFinite(this.parallelUnit.quantity)) {
      console.warn(
        `${LOG_PREFIX} WARNING: parallelUnit.quantity is Infinity or -Infinity.`,
        { quantity: this.parallelUnit.quantity }
      );
    }

    // 3. baseUnit and parallelUnit with same unitId (redundant)
    if (this.parallelUnit && this.baseUnit && this.parallelUnit.unitId === this.baseUnit.unitId) {
      console.warn(
        `${LOG_PREFIX} WARNING: parallelUnit has the same unitId as baseUnit. This is redundant and should be avoided.`,
        {
          baseUnit: { id: this.baseUnit.unitId, name: this.baseUnit.unitName },
          parallelUnit: { id: this.parallelUnit.unitId, name: this.parallelUnit.unitName }
        }
      );
    }

    // 4. UMP data exists but flags disabled (historical mode)
    if (!this.validateFlags &&
        this.parallelUnit &&
        this.isValidQuantity(this.parallelUnit.quantity) &&
        this.material &&
        (!this.companyUMPActive || !this.material.UMPActive)) {
      console.warn(
        `${LOG_PREFIX} INFO: Parallel unit data exists but UMP flags indicate it's disabled. ` +
        `Data will be shown because validateFlags=false (historical mode).`,
        {
          parallelUnitQuantity: this.parallelUnit.quantity,
          companyUMPActive: this.companyUMPActive,
          materialUMPActive: this.material.UMPActive,
          validateFlags: this.validateFlags,
          suggestion: 'Set validateFlags=true to respect current UMP configuration and hide historical data'
        }
      );
    }

    // 5. validateFlags=true but no UMP data (flags active without data)
    if (this.validateFlags &&
        this.material &&
        this.companyUMPActive &&
        this.material.UMPActive &&
        !this.parallelUnit) {
      console.warn(
        `${LOG_PREFIX} INFO: UMP is enabled (both company and material flags are true) ` +
        `but parallelUnit data is not provided.`,
        {
          companyUMPActive: this.companyUMPActive,
          materialUMPActive: this.material.UMPActive,
          parallelUnit: this.parallelUnit
        }
      );
    }

    // 6. decimalPlaces out of reasonable range
    if (this.decimalPlaces < 0 || this.decimalPlaces > 10) {
      console.warn(
        `${LOG_PREFIX} WARNING: decimalPlaces value is unusual. Expected 0-10.`,
        { received: this.decimalPlaces }
      );
    }

    // 7. usedUnit same as baseUnit (shouldShowUsedUnit will be false)
    if (this.usedUnit && this.baseUnit && this.usedUnit.unitId === this.baseUnit.unitId) {
      console.warn(
        `${LOG_PREFIX} INFO: usedUnit has the same unitId as baseUnit. It will not be displayed (smart hiding).`,
        {
          usedUnit: { id: this.usedUnit.unitId, name: this.usedUnit.unitName },
          baseUnit: { id: this.baseUnit.unitId, name: this.baseUnit.unitName }
        }
      );
    }
  }

  /**
   * DATA COHERENCE VALIDATIONS
   * Detects data inconsistencies that could indicate upstream errors
   * @private
   */
  private validateDataCoherence(): void {
    const LOG_PREFIX = '[UnitMeasureDisplay]';

    // 1. usedUnit same as baseUnit in data but different ID
    if (this.usedUnit && this.baseUnit) {
      const sameQuantity = this.usedUnit.quantity === this.baseUnit.quantity;
      const sameName = this.usedUnit.unitName === this.baseUnit.unitName;
      const differentId = this.usedUnit.unitId !== this.baseUnit.unitId;

      if (sameQuantity && sameName && differentId) {
        console.warn(
          `${LOG_PREFIX} WARNING: usedUnit appears to be the same as baseUnit (same quantity and name) ` +
          `but has different unitId. This might indicate duplicate unit data.`,
          {
            usedUnit: this.usedUnit,
            baseUnit: this.baseUnit
          }
        );
      }
    }

    // 2. parallelUnit without conversion factor (incomplete tooltip)
    if (this.parallelUnit &&
        this.isValidQuantity(this.parallelUnit.quantity) &&
        !this.parallelUnit.conversionFactor) {
      console.warn(
        `${LOG_PREFIX} INFO: parallelUnit is missing conversionFactor. ` +
        `Tooltip will not show conversion information.`,
        { parallelUnit: this.parallelUnit }
      );
    }

    // 3. Validate MongoDB ObjectId format (24 hex characters)
    const mongoIdRegex = /^[a-f\d]{24}$/i;

    if (this.baseUnit && this.baseUnit.unitId && !mongoIdRegex.test(this.baseUnit.unitId)) {
      console.warn(
        `${LOG_PREFIX} WARNING: baseUnit.unitId does not match MongoDB ObjectId format (24 hex chars).`,
        {
          received: this.baseUnit.unitId,
          suggestion: 'Ensure the ID is a valid MongoDB ObjectId string'
        }
      );
    }

    if (this.parallelUnit && this.parallelUnit.unitId && !mongoIdRegex.test(this.parallelUnit.unitId)) {
      console.warn(
        `${LOG_PREFIX} WARNING: parallelUnit.unitId does not match MongoDB ObjectId format (24 hex chars).`,
        {
          received: this.parallelUnit.unitId,
          suggestion: 'Ensure the ID is a valid MongoDB ObjectId string'
        }
      );
    }

    if (this.usedUnit && this.usedUnit.unitId && !mongoIdRegex.test(this.usedUnit.unitId)) {
      console.warn(
        `${LOG_PREFIX} WARNING: usedUnit.unitId does not match MongoDB ObjectId format (24 hex chars).`,
        {
          received: this.usedUnit.unitId,
          suggestion: 'Ensure the ID is a valid MongoDB ObjectId string'
        }
      );
    }

    if (this.material && this.material._id && !mongoIdRegex.test(this.material._id)) {
      console.warn(
        `${LOG_PREFIX} WARNING: material._id does not match MongoDB ObjectId format (24 hex chars).`,
        {
          received: this.material._id,
          suggestion: 'Ensure the ID is a valid MongoDB ObjectId string'
        }
      );
    }

    // 4. Validate coherence between quantities and conversion factor
    if (this.parallelUnit &&
        this.isValidQuantity(this.baseUnit.quantity) &&
        this.isValidQuantity(this.parallelUnit.quantity) &&
        this.parallelUnit.conversionFactor) {

      // Calculate expected parallel quantity based on base quantity and conversion factor
      const expectedParallelQty = this.baseUnit.quantity * this.parallelUnit.conversionFactor;
      const tolerance = 0.01; // 1% tolerance for rounding errors
      const difference = Math.abs(this.parallelUnit.quantity - expectedParallelQty);
      const percentDifference = (difference / expectedParallelQty) * 100;

      if (percentDifference > tolerance) {
        console.warn(
          `${LOG_PREFIX} WARNING: parallelUnit.quantity does not match expected value based on ` +
          `baseUnit.quantity and conversionFactor. This might indicate data inconsistency.`,
          {
            baseQuantity: this.baseUnit.quantity,
            parallelQuantity: this.parallelUnit.quantity,
            conversionFactor: this.parallelUnit.conversionFactor,
            expectedParallelQuantity: expectedParallelQty,
            difference: difference,
            percentDifference: `${percentDifference.toFixed(2)}%`
          }
        );
      }
    }
  }

  /**
   * Smart display logic: Show used unit only if it's different from base unit
   * Avoids redundancy when the sold/used unit is the same as UMB
   *
   * @returns true if usedUnit should be displayed
   */
  get shouldShowUsedUnit(): boolean {
    if (!this.usedUnit) {
      return false;
    }
    return this.usedUnit.unitId !== this.baseUnit.unitId;
  }

  /**
   * Determines if UMP (Parallel Unit) should be displayed
   * Supports two modes: default (data-based) and strict (flag-based)
   *
   * Default mode (validateFlags = false):
   * - Shows UMP if parallelUnit.quantity is a valid number (including 0)
   * - Preserves historical information even if flags change later
   *
   * Strict mode (validateFlags = true):
   * - Shows UMP only if both companyUMPActive and material.UMPActive are true
   * - Respects current configuration
   *
   * @returns true if UMP should be displayed
   */
  get shouldShowUMP(): boolean {
    if (this.validateFlags) {
      // Strict mode: validate flags
      // PROTECTION: Check that material exists before accessing UMPActive
      if (!this.material) {
        if (!environment.production) {
          console.error('[UnitMeasureDisplay] Cannot check shouldShowUMP: material is null/undefined');
        }
        return false;
      }

      return (
        this.companyUMPActive &&
        this.material.UMPActive &&
        this.parallelUnit !== undefined
      );
    } else {
      // Default mode: check if data exists
      return this.isValidQuantity(this.parallelUnit?.quantity) && !!this.parallelUnit.unitId;
    }
  }

  /**
   * Validates if a quantity value is a valid number for display
   * Treats 0 as valid, but null/undefined/NaN as invalid
   *
   * @param quantity - The quantity value to validate
   * @returns true if quantity is a valid number (including 0)
   */
  isValidQuantity(quantity: number | null | undefined): boolean {
    return (
      quantity !== null &&
      quantity !== undefined &&
      !isNaN(quantity) &&
      typeof quantity === 'number'
    );
  }

  /**
   * Gets the display value for UMP
   * Returns formatted quantity or '-' if data is missing but should be shown
   *
   * @returns Formatted UMP display string
   */
  get umpDisplayValue(): string {
    if (this.parallelUnit && this.isValidQuantity(this.parallelUnit.quantity)) {
      return this.getFormattedQuantity(this.parallelUnit.quantity);
    }
    return '-';
  }

  /**
   * Formats a quantity number with configurable decimal places
   * Returns '??' if the quantity is invalid
   *
   * @param quantity - The quantity to format
   * @returns Formatted quantity string (e.g., "288.000") or "??"
   */
  getFormattedQuantity(quantity: number | undefined | null): string {
    if (!this.isValidQuantity(quantity)) {
      return '??';
    }

    const decimalFormat = `1.${this.decimalPlaces}-${this.decimalPlaces}`;
    return this.decimalPipe.transform(quantity!, decimalFormat) ?? '??';
  }

  /**
   * Generates tooltip content for base unit (UMB)
   *
   * @returns Tooltip text for base unit
   */
  get baseUnitTooltip(): string {
    return `Unidad de Medida Base: ${this.baseUnit.unitName}`;
  }

  /**
   * Generates tooltip content for parallel unit (UMP)
   * Includes conversion factor if available
   *
   * @returns Tooltip text for parallel unit
   */
  get parallelUnitTooltip(): string {
    if (!this.parallelUnit) {
      return '';
    }

    let tooltip = `Unidad de Medida Paralela: ${this.parallelUnit.unitName}`;

    if (this.parallelUnit.conversionFactor) {
      tooltip += `\nFactor de conversión: 1 UMB = ${this.parallelUnit.conversionFactor} UMP`;
    }

    return tooltip;
  }

  /**
   * Generates tooltip content for used unit
   *
   * @returns Tooltip text for used unit
   */
  get usedUnitTooltip(): string {
    if (!this.usedUnit) {
      return '';
    }
    return `Unidad Utilizada: ${this.usedUnit.unitName}`;
  }
}
